import {
    CommonProps,
    Portal,
    classNames,
    documentObject,
    getKey,
    mergeRefs,
    useId,
    waitForRender,
    windowObject,
} from '@fold-dev/core'
import chrono from 'chrono-node'
import { forwardRef, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LabelSelectLabel, UserSelectUser } from '../'
import { isOffScreenX, isOffScreenY, openLinkInText, setEndOfContentEditable, stopEvent } from '../../helpers'
import { RichInputContext } from './rich-input.provider'
import {
    createDateNode,
    findMarkdownFormats,
    getNodeAtCursor,
    getWindowSelection,
    getWordAtCaret,
    insertEntity,
    moveCursorToEnd,
    parseNLPDate,
    selectAll,
    unwrapDataSetDate,
    unwrapNode,
    useRichInputEvent,
} from './rich-input.util'

export const OptionsMenu = ({ position, options, onSelect, index }) => {
    const [offscreenX, setOffscreenX] = useState(false)
    const [offscreenY, setOffscreenY] = useState(false)
    const ref = useRef(null)

    useLayoutEffect(() => {
        setOffscreenX(isOffScreenX(ref.current, position))
        setOffscreenY(isOffScreenY(ref.current, position))
    }, [position])

    return (
        <Portal>
            <div
                ref={ref}
                className="f-rich-input__menu"
                style={{
                    top: position.y,
                    left: position.x,
                    display: options.length == 0 ? 'none' : 'block',
                    transform:
                        offscreenX && offscreenY
                            ? 'translateX(-100%) translateY(-100%)'
                            : offscreenX
                            ? 'translateX(-100%)'
                            : offscreenY
                            ? 'translateY(-100%)'
                            : null,
                }}>
                {options.map(({ id, name }, index1: number) => (
                    <div
                        key={index1}
                        className="f-rich-input__menu-item f-row f-buttonize"
                        onClick={() => onSelect({ id, name })}
                        style={{ background: index == index1 ? 'var(--f-color-surface-strong)' : 'transparent' }}>
                        {name}
                    </div>
                ))}
            </div>
        </Portal>
    )
}

export type RichInputOption = {
    id: string
    name: string
    type: string
}

export type HtmlProcessorResult = {
    labels?: LabelSelectLabel
    users?: UserSelectUser
    title: string
    from?: Date
    to?: Date
}

export const RICH_EDITOR_FOCUS_DELAY = 100

export type RichInputProps = {
    selectOnFocus?: boolean
    dontClearOnSubmit?: boolean
    defaultValue?: string
    placeholder?: string
    className?: string
    onSubmit: (value) => void
    onEnter?: (value) => void
    onCancel?: () => void
    onBlur?: (value) => void
    onIndent?: () => void
    onOutdent?: () => void
    onUp?: () => void
    onDown?: () => void
    onPasteMultiLine?: (e) => void
} & CommonProps

export const RichInput = forwardRef((props: RichInputProps, ref) => {
    const {
        selectOnFocus,
        dontClearOnSubmit,
        defaultValue = '',
        placeholder = '',
        disabled,
        onSubmit,
        onEnter,
        onCancel,
        onBlur,
        onIndent,
        onOutdent,
        onUp,
        onDown,
        onPasteMultiLine,
        ...rest
    } = props
    const id = useId()
    const editorRef = useRef(null)
    const blockHighlightRef = useRef([])
    const rangeCache = useRef(null)
    const { triggers, options, onTrigger } = useContext(RichInputContext)
    const [html, setHtml] = useState(defaultValue)
    const [timestamp, setTimestamp] = useState(0)
    const empty = useMemo(() => editorRef.current?.innerHTML.trim() == '', [timestamp, html])
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [index, setIndex] = useState(0)
    const menuOpen = useMemo(() => !!options.length && !!position.x && !!position.y, [options, position])
    const className = classNames({ 'f-rich-input': true, 'is-disabled': disabled }, [props.className])
    const dateTimeout = useRef(null)

    const updateTimestamp = () => {
        setTimestamp(new Date().getTime())
    }

    const focus = () => {
        editorRef.current?.focus()
    }

    const openMenu = () => {
        const selection: Selection = getWindowSelection()
        const range: Range = selection.getRangeAt(0)

        if (!!range.getClientRects().length) {
            const { x, y } = range.getClientRects()[0]
            rangeCache.current = range
            setPosition({ x, y })
        }
    }

    const closeMenu = () => {
        rangeCache.current = null
        setPosition({ x: 0, y: 0 })
    }

    const handleInput = (e) => {
        stopEvent(e)
        updateTimestamp()
    }

    const handlePaste = (e) => {
        e.preventDefault()
        const paste = e.clipboardData.getData('text')

        if (/\r?\n/.test(paste)) {
            if (onPasteMultiLine) onPasteMultiLine(paste)
        } else {
            const selection = windowObject.getSelection()
            if (!selection.rangeCount) return
            selection.deleteFromDocument()
            selection.getRangeAt(0).insertNode(documentObject.createTextNode(paste))
            selection.collapseToEnd()
            updateTimestamp()
        }
    }

    const handleKeyUp = (e) => {
        clearTimeout(dateTimeout.current)

        const { isSpace, isEnter, isBackspace, isEscape } = getKey(e)
        const word = getWordAtCaret()
        const firstChar = word?.trim().charAt(0)
        const node = getNodeAtCursor()

        if (isBackspace) {
            if (node?.className == 'f-rich-input-entity') {
                blockHighlightRef.current = [...blockHighlightRef.current, node.textContent]
                stopEvent(e)
                unwrapNode(node)
            }
        }

        if (triggers.includes(firstChar)) {
            onTrigger(word)
            openMenu()
        } else {
            closeMenu()
        }

        if (isSpace) {
            // pseudo markdown ----------------------------
            // --------------------------------------------
            // --------------------------------------------
            // --------------------------------------------

            const selection: Selection = getWindowSelection()
            const range = selection.getRangeAt(0)
            const { startContainer } = range
            const { nodeValue } = startContainer
            const markdownMatches = findMarkdownFormats(nodeValue)

            if (markdownMatches.length) {
                const match = markdownMatches[0]
                const { tag, isLink, isArrow } = match

                if (isArrow) {
                    const { fullMatch, totalLength, matchIndex } = match

                    const before = nodeValue.slice(0, matchIndex)
                    const after = nodeValue.slice(matchIndex + totalLength)
                    const newText = before + '→ ' + after

                    startContainer.nodeValue = newText

                    const selection = documentObject.getSelection()
                    const afterRange = documentObject.createRange()
                    afterRange.setStart(startContainer, before.length + 2)
                    afterRange.collapse(true)
                    selection.removeAllRanges()
                    selection.addRange(afterRange)
                } else if (isLink) {
                    const { url, fullMatch, totalLength, matchIndex } = match

                    const before = nodeValue.slice(0, matchIndex)
                    const after = nodeValue.slice(matchIndex + totalLength)

                    const linkText = fullMatch.slice(1, fullMatch.indexOf(']'))

                    const newText = before + linkText + after

                    startContainer.nodeValue = newText

                    const innerStart = before.length
                    const innerEnd = innerStart + linkText.length

                    const formatRange = documentObject.createRange()
                    formatRange.setStart(startContainer, innerStart)
                    formatRange.setEnd(startContainer, innerEnd)

                    const element = documentObject.createElement(tag)
                    element.setAttribute('href', url)
                    formatRange.surroundContents(element)

                    const spaceNode = documentObject.createTextNode(' ')
                    element.parentNode.insertBefore(spaceNode, element.nextSibling)

                    const selection = documentObject.getSelection()
                    const afterRange = documentObject.createRange()

                    afterRange.setStart(spaceNode, 1)
                    afterRange.collapse(true)
                    selection.removeAllRanges()
                    selection.addRange(afterRange)
                } else {
                    const { start, end, offset } = match

                    const before = nodeValue.slice(0, start - offset)
                    const innerText = nodeValue.slice(start, end)
                    const after = nodeValue.slice(end + offset)
                    const newText = before + innerText + after

                    startContainer.nodeValue = newText

                    const innerStart = before.length
                    const innerEnd = innerStart + innerText.length

                    const formatRange = documentObject.createRange()
                    formatRange.setStart(startContainer, innerStart)
                    formatRange.setEnd(startContainer, innerEnd)

                    const element = documentObject.createElement(tag)
                    formatRange.surroundContents(element)

                    const spaceNode = documentObject.createTextNode(' ')
                    element.parentNode.insertBefore(spaceNode, element.nextSibling)

                    const selection = documentObject.getSelection()
                    const afterRange = documentObject.createRange()

                    afterRange.setStart(spaceNode, 1)
                    afterRange.collapse(true)
                    selection.removeAllRanges()
                    selection.addRange(afterRange)
                }
            }

            // --------------------------------------------
            // --------------------------------------------
            // --------------------------------------------

            dateTimeout.current = setTimeout(() => {
                const ignoreList = blockHighlightRef.current
                const selection: Selection = getWindowSelection()
                const range = selection.getRangeAt(0)
                const { startContainer, startOffset, endOffset } = range
                const { nodeValue } = startContainer

                const parsedDate: any = parseNLPDate(nodeValue, ignoreList)

                if (parsedDate) {
                    unwrapDataSetDate(editorRef.current)

                    const { startIndex, endIndex, match } = parsedDate
                    const range1 = documentObject.createRange()

                    range1.setStart(startContainer, startIndex)
                    range1.setEnd(startContainer, endIndex)

                    const entity = createDateNode('default')

                    range1.surroundContents(entity)
                    range1.collapse(true)
                } else {
                    const dates = chrono.parse(nodeValue)
                    const date = dates[dates.length - 1]

                    if (date) {
                        unwrapDataSetDate(editorRef.current)

                        const { index, ref, tags } = date
                        const text = date.text.trim()

                        const startIndex = index
                        const endIndex = startIndex + text.length
                        const range1 = documentObject.createRange()

                        // TODO:
                        // integrate ignore list!
                        // don't highlight previous unhighlighted dates
                        if (blockHighlightRef.current.includes(text)) return

                        range1.setStart(startContainer, startIndex)
                        range1.setEnd(startContainer, endIndex)

                        const entity = createDateNode('chrono')

                        range1.surroundContents(entity)
                        range1.collapse(true)
                    }
                }
            }, 500)
        }
    }

    const handleClick = (e) => {
        stopEvent(e)
    }

    const submit = (refocus) => {
        editorRef.current.style.caretColor = 'transparent'
        onSubmit(editorRef.current.innerHTML)

        blockHighlightRef.current = []

        if (!dontClearOnSubmit) {
            editorRef.current.innerHTML = ''
            setHtml('')
            updateTimestamp()
        }

        if (refocus) {
            waitForRender(() => {
                focus()
            })
        }

        waitForRender(() => {
            if (editorRef.current) {
                if (refocus) moveCursorToEnd(editorRef.current)
                editorRef.current.style.caretColor = 'auto'
            }
        }, RICH_EDITOR_FOCUS_DELAY)
    }

    const handleKeyDown = (e) => {
        const inputEvent: KeyboardEvent = e.nativeEvent
        const { metaKey, key } = inputEvent
        const { isEnter, isRight, isLeft, isUp, isDown, isShift, isBackspace, isEscape, isSpace, isMeta } = getKey(e)

        if (isEscape && !menuOpen) {
            if (onCancel) onCancel()
            return
        }

        if (isEscape && menuOpen) {
            stopEvent(e)
            closeMenu()
            waitForRender(() => {
                focus()
                setEndOfContentEditable(editorRef.current)
            }, 100)
            return
        }

        if (isEnter && !menuOpen) {
            stopEvent(e)
            submit(true)
        }

        if (isEnter && menuOpen) {
            stopEvent(e)
        }

        if (isMeta && isShift && isRight) {
            stopEvent(e)
            if (onIndent) onIndent()
        }

        if (isMeta && isShift && isLeft) {
            stopEvent(e)
            if (onOutdent) onOutdent()
        }

        if (isMeta && isShift && isUp) {
            stopEvent(e)
            if (onUp) onUp()
        }

        if (isMeta && isShift && isDown) {
            stopEvent(e)
            if (onDown) onDown()
        }

        if (menuOpen) {
            if (isUp) {
                stopEvent(e)
                setIndex(index == 0 ? options.length - 1 : index - 1)
            }

            if (isDown) {
                stopEvent(e)
                setIndex(index == options.length - 1 ? 0 : index + 1)
            }

            if (isEnter) {
                stopEvent(e)
                insertEntity(options[index], rangeCache.current, () => {
                    closeMenu()
                })
            }
        }

        if (metaKey) {
            switch (key) {
                case 'b':
                    return stopEvent(e)
                case 's':
                    return stopEvent(e)
                case 'i':
                    return stopEvent(e)
            }
        }
    }

    const handleFocus = (e) => {}

    const handleBlur = (e) => {
        submit(false)
    }

    const handleMouseDown = (e) => {
        openLinkInText(e, () => {
            if (e.target.dataset.entity) {
                e.preventDefault()
                e.target.focus()
            }
        })
    }

    const handleOnEnterEvent = (e) => {
        if (props.id == e.detail.id) submit(true)
    }

    useRichInputEvent('onenter', handleOnEnterEvent)

    useEffect(() => {
        updateTimestamp()
    }, [html])

    useEffect(() => {
        setHtml(defaultValue)
        editorRef.current.innerHTML = defaultValue
    }, [defaultValue])

    useEffect(() => {
        setIndex(0)
    }, [options.length])

    useEffect(() => {
        setTimeout(() => {
            if (selectOnFocus) {
                selectAll(editorRef.current)
            } else {
                moveCursorToEnd(editorRef.current)
            }
        })
    }, [selectOnFocus])

    return (
        <div
            {...rest}
            className={className}>
            {empty && <div className="f-rich-input__placeholder">{placeholder}</div>}

            <div
                id={id}
                tabIndex={0}
                ref={mergeRefs([editorRef, ref])}
                className="f-rich-input__input"
                contentEditable={!disabled}
                suppressContentEditableWarning
                spellCheck={false}
                dangerouslySetInnerHTML={{ __html: html }}
                onMouseDown={handleMouseDown}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onFocus={handleFocus}
                onInput={handleInput}
                onPaste={handlePaste}
                onBlur={handleBlur}
                onClick={handleClick}
            />

            {menuOpen && (
                <OptionsMenu
                    position={position}
                    index={index}
                    options={options}
                    onSelect={insertEntity}
                />
            )}
        </div>
    )
})
